import { CalculatorOutlined, FieldTimeOutlined, GatewayOutlined } from '@ant-design/icons';
import { Alert, List, Space, Spin, Typography } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import {
  Card,
  CardType,
  CardUser,
  convertCardUserToCard,
  getCardDurationDesc,
  getCardExpiredDesc,
  getCardTypeDesc,
} from '@/models/card';
import { useRequestSpecificationList } from '@/models/sku';
import { POSBaseInfoContext } from '../LayoutPOS/Synchronizer';
import PosButton from '../PosButton';
import PosPopover from '../PosPopover';
import Price from '../Price';
import styles from './style.less';

export const CardSkuInfo = ({ card: cardOrCardUser }: { card: Card | CardUser }) => {
  const card: Card =
    'cardId' in cardOrCardUser ? convertCardUserToCard(cardOrCardUser) : cardOrCardUser;

  const { getSkuSpecificationById } = useContext(POSBaseInfoContext);
  const { specificationId: specificationIdList } = card;
  const [goodsSpecificationList, updateGoodsSpecificationList] = useState(
    specificationIdList.map(id => getSkuSpecificationById(id)).filter(Boolean)
  );

  const {
    payload: goodsSpecificationListRemote,
    send,
    loading,
    loaded,
    error,
  } = useRequestSpecificationList();

  useEffect(() => {
    if (!goodsSpecificationList.length && !goodsSpecificationListRemote && !loaded && !error) {
      send(card.specificationId).then(v => {
        updateGoodsSpecificationList(v);
      });
    }
  }, [goodsSpecificationList, card, goodsSpecificationListRemote, loaded, error]);

  if (loading) {
    return (
      <div className="text-center">
        <Spin />
      </div>
    );
  }

  if (!goodsSpecificationList.length) {
    if (error) {
      return <div className="text-center">{error.msg}</div>;
    } else if (!goodsSpecificationListRemote?.length) {
      return <div className="text-center">未关联商品，请检查</div>;
    }
  }

  return (
    <div>
      <Alert
        message={
          <>
            {goodsSpecificationList.length > 1 ? (
              <>
                以下项目任选1份
                {card.type === CardType.TIMES ? (
                  <>
                    ，共<b> {card.times} </b>次
                  </>
                ) : (
                  <>，有效期内不限次数</>
                )}
              </>
            ) : null}

            {goodsSpecificationList.length === 1 ? (
              <>
                包含以下项目
                {card.type === CardType.TIMES ? (
                  <>
                    <b> {card.times}</b> 次服务
                  </>
                ) : (
                  <>，有效期内不限次数</>
                )}
              </>
            ) : null}
          </>
        }
      />

      <List
        size="small"
        dataSource={goodsSpecificationList}
        renderItem={record => (
          <List.Item style={{ paddingLeft: 0, paddingRight: 0 }}>
            <List.Item.Meta
              title={<>{record.goodsName}</>}
              description={
                <Space direction="horizontal" size={5}>
                  <div>规格: 【{record.name || '默认'}】</div>
                  <div>
                    实价: <Price value={record.price} />
                  </div>
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );
};

export const CardSkuInfoPopover = ({
  card,
  children,
}: {
  card: Card | CardUser;
  children: React.ReactChild;
}) => {
  const pureCard: Card = 'cardId' in card ? convertCardUserToCard(card) : card;
  return (
    <PosPopover
      destroyOnHidden
      autoAdjustOverflow
      arrow
      trigger={'click'}
      title={
        <>
          <GatewayOutlined /> {pureCard.name}
        </>
      }
      content={
        <div
          style={{ minWidth: 260 }}
          onClick={e => {
            e.stopPropagation();
          }}>
          <CardSkuInfo card={pureCard} />
        </div>
      }>
      {children}
    </PosPopover>
  );
};

type Props = {
  card: Card;
  onClick?: (card: Card) => void;
};

const CardView: React.FC<Props> = ({ card, onClick = () => {} }: Props) => {
  return (
    <div
      className={`${styles.container} ${styles[`type-${card.type}`]}`}
      onClick={() => {
        onClick(card);
      }}>
      <div className={`${styles.left} `}>
        <div className={styles.price}>
          <i>&yen;</i>
          {card.price}
        </div>
        <div className={styles.type}>{getCardTypeDesc(card)}</div>
      </div>
      <div className={styles.main}>
        <Typography.Title className={styles.name} ellipsis={{ rows: 1 }}>
          {card.name}
        </Typography.Title>
        <Typography.Paragraph className={styles.intro} ellipsis={{ rows: 2 }}>
          {card.intro}
        </Typography.Paragraph>
        {card.type === CardType.TIMES && (
          <Space size={10} direction="vertical">
            <div>
              <CalculatorOutlined /> 次数: {card.times}
            </div>
            <div>
              <FieldTimeOutlined /> 效期: {getCardExpiredDesc(card)}
            </div>
          </Space>
        )}
        {card.type === CardType.DURATION && (
          <div>
            <div>
              <FieldTimeOutlined /> 时长: {getCardDurationDesc(card)}
            </div>
          </div>
        )}
        <div style={{ paddingTop: 10 }}>
          <CardSkuInfoPopover card={card}>
            <PosButton
              size="middle"
              icon={<GatewayOutlined />}
              onClick={e => {
                e.stopPropagation();
              }}>
              查看适用范围
            </PosButton>
          </CardSkuInfoPopover>
        </div>
      </div>
    </div>
  );
};

export default CardView;
